import React, { useMemo } from 'react'
import { useHRStore } from '../store/hrStore'
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { TrendingUp, Users, Award, BarChart3, PieChart as PieChartIcon } from 'lucide-react'

export default function HRAnalyticsPage() {
  const { employees, departments, getTotalEmployees, getAveragePerformanceRating, getAttendanceRate } = useHRStore()

  // Department distribution
  const deptDistribution = useMemo(() => {
    return departments.map((d) => ({
      name: d.name,
      value: d.headcount,
    }))
  }, [departments])

  // Performance ratings
  const performanceData = useMemo(() => {
    return [
      { rating: '5.0', count: employees.filter((e) => e.performanceRating >= 4.8).length },
      { rating: '4.5', count: employees.filter((e) => e.performanceRating >= 4.3 && e.performanceRating < 4.8).length },
      { rating: '4.0', count: employees.filter((e) => e.performanceRating >= 3.8 && e.performanceRating < 4.3).length },
      { rating: '3.5', count: employees.filter((e) => e.performanceRating >= 3.3 && e.performanceRating < 3.8).length },
      { rating: '3.0', count: employees.filter((e) => e.performanceRating < 3.3).length },
    ]
  }, [employees])

  // Salary distribution
  const salaryRanges = useMemo(() => {
    return [
      { range: '$0-50K', count: employees.filter((e) => e.salary < 50000).length },
      { range: '$50-75K', count: employees.filter((e) => e.salary >= 50000 && e.salary < 75000).length },
      { range: '$75-100K', count: employees.filter((e) => e.salary >= 75000 && e.salary < 100000).length },
      { range: '$100-150K', count: employees.filter((e) => e.salary >= 100000 && e.salary < 150000).length },
      { range: '$150K+', count: employees.filter((e) => e.salary >= 150000).length },
    ]
  }, [employees])

  // Department stats
  const deptStats = useMemo(() => {
    return departments.map((d) => {
      const deptEmps = employees.filter((e) => e.department === d.name)
      const avgSalary = deptEmps.length > 0 ? deptEmps.reduce((sum, e) => sum + e.salary, 0) / deptEmps.length : 0
      const avgRating = deptEmps.length > 0 ? (deptEmps.reduce((sum, e) => sum + e.performanceRating, 0) / deptEmps.length).toFixed(1) : 0
      return {
        name: d.name,
        headcount: d.headcount,
        avgSalary: Math.round(avgSalary),
        avgRating: parseFloat(avgRating),
      }
    })
  }, [employees, departments])

  const avgPerformance = useMemo(() => getAveragePerformanceRating(), [getAveragePerformanceRating])
  const attendanceRate = useMemo(() => getAttendanceRate(), [getAttendanceRate])
  const totalEmployees = useMemo(() => getTotalEmployees(), [getTotalEmployees])
  const totalPayroll = useMemo(() => employees.reduce((sum, e) => sum + e.salary, 0), [employees])
  const avgSalary = useMemo(() => (totalEmployees > 0 ? (totalPayroll / totalEmployees).toFixed(0) : 0), [totalEmployees, totalPayroll])

  const COLORS = ['#0284c7', '#06b6d4', '#8b5cf6', '#ec4899', '#f59e0b']

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">HR Analytics & Insights</h1>
          <p className="text-slate-400">Data-driven insights on employee metrics, turnover, and organizational health</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Employees</h3>
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-white">{totalEmployees}</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Avg Performance</h3>
              <Award className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white">{avgPerformance}/5.0</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Attendance Rate</h3>
              <TrendingUp className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">{attendanceRate}%</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 hover:border-yellow-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Payroll</h3>
              <BarChart3 className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-2xl font-bold text-white">${(totalPayroll / 1000000).toFixed(1)}M</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6 hover:border-purple-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Avg Salary</h3>
              <PieChartIcon className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-2xl font-bold text-white">${avgSalary}K</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Department Distribution */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Department Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={deptDistribution} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {COLORS.map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Performance Rating Distribution */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Performance Ratings</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="rating" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #10b981' }} />
                <Bar dataKey="count" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Salary Distribution */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Salary Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={salaryRanges}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="range" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #06b6d4' }} />
                <Bar dataKey="count" fill="#06b6d4" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Department Comparison */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4">Department Analytics</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={deptStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #f59e0b' }} />
                <Legend />
                <Bar dataKey="headcount" fill="#f59e0b" />
                <Bar dataKey="avgRating" fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Department Table */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Department Details</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Department</th>
                  <th className="text-center py-3 px-4 text-blue-300 font-semibold">Headcount</th>
                  <th className="text-right py-3 px-4 text-blue-300 font-semibold">Avg Salary</th>
                  <th className="text-center py-3 px-4 text-blue-300 font-semibold">Avg Rating</th>
                  <th className="text-right py-3 px-4 text-blue-300 font-semibold">Budget</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((dept) => (
                  <tr key={dept.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                    <td className="py-3 px-4 text-white font-semibold">{dept.name}</td>
                    <td className="py-3 px-4 text-center text-cyan-400">{dept.headcount}</td>
                    <td className="py-3 px-4 text-right text-green-400">${(deptStats.find((d) => d.name === dept.name)?.avgSalary / 1000).toFixed(0)}K</td>
                    <td className="py-3 px-4 text-center text-yellow-400">{deptStats.find((d) => d.name === dept.name)?.avgRating || 0}/5.0</td>
                    <td className="py-3 px-4 text-right text-blue-400">${(dept.budget / 1000000).toFixed(1)}M</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
